// Inline, cell-sized editor for a single transaction column — shared by the
// main multi-tabular grid (EmbedCaptureTabularPreview) and the inline child
// grid (ChildTabularGrid) so both render cells identically. Extracted verbatim
// from EmbedCaptureTabularPreview; no behaviour change.

import { useState } from 'react'
import { formatForInput, parseTypedDate, withLocalZone } from '../../dateInput'
import { Search } from 'lucide-react'

import type { ColumnDef } from '../types'
import {
  AUDIT_TOKEN_LABELS, auditTokenOf, boolCheckValues, columnDocType, ddlbSelectValue,
  fileUploadAccept, hasBoolCheck, hasCheckboxValues, hasDropdown, hasFileUpload,
  hasImageUpload, hasLookup, hasRadioValues, hasUrlWidget, inputType,
  isAutoGenerate, isBoolCheckOn, isKey, isProtect, projectVarOf, isRichTextColumn,
  isZonedColumn, applyTextCase, listHeadingOf,
} from '../utils'
import LookupModal from '../LookupModal'
import FileUploadField from '../widgets/FileUploadField'
import UrlField from '../widgets/UrlField'
import { RichTextCellEditor } from '../widgets/RichTextField'
import DateField from '../widgets/DateField'
import { NumericInput } from '../widgets/FormatFields'

export function InlineCellEditor({
  project, objName, col, value, error, readOnlyKey, viewOnly, onChange: onChangeRaw, rowValues, headerValues, uploadDocument,
}: {
  project: string
  objName: string
  col: ColumnDef
  value: unknown
  error?: string
  readOnlyKey: boolean
  // The whole row is locked (engine edit-lock rule / awaiting approval) ⇒ every
  // cell is read-only, regardless of its own protect flag.
  viewOnly?: boolean
  onChange: (v: unknown) => void
  uploadDocument: (file: File) => Promise<{ url: string; name?: string }>
  // Other cells in the current row — forwarded to LookupModal so a
  // lookup_filter like ``dept = {form.DEPT_CODE}`` can resolve against
  // the same-row context.
  rowValues?: Record<string, unknown>
  /** The parent header's values, for a lookup reading a header field. */
  headerValues?: Record<string, unknown>
}) {
  // Typed text takes the column's case (``text_case``) as it is entered.
  const onChange = (v: unknown) => onChangeRaw(typeof v === 'string' ? applyTextCase(col, v) : v)
  const disabled = !!viewOnly || isProtect(col) || (readOnlyKey && isKey(col))
  const type = inputType(col)
  const auditToken = auditTokenOf(col)
  const projectVar = projectVarOf(col)
  const autoGen = isAutoGenerate(col)
  const placeholder = auditToken
    ? 'auto (server)'
    : autoGen
      ? 'auto-generate'
      : projectVar
        ? `:${projectVar}`
        : (col.default_value || '')
  const title = auditToken
    ? AUDIT_TOKEN_LABELS[auditToken]
    : autoGen
      ? 'Auto-generated serial on save'
      : projectVar
        ? `Project variable :${projectVar}`
        : ''
  // Lookup is best-effort: button only shows when the column declares
  // one. LookupModal internally handles network/server failures and
  // surfaces them to the user without blocking the cell — they can
  // still type a value manually.
  const showLookup = hasLookup(col) && !hasDropdown(col)
  const [lookupOpen, setLookupOpen] = useState(false)

  const baseStyle = cellInputStyle(!!error, disabled)

  if (isRichTextColumn(col)) {
    return (
      <RichTextCellEditor
        project={project}
        objName={objName}
        value={value}
        onChange={v => onChange(v)}
        disabled={disabled}
        heading={listHeadingOf(col)}
        hasError={!!error}
      />
    )
  }

  if (hasDropdown(col)) {
    return (
      <select
        value={ddlbSelectValue(value, col.edit_mask!.values!)}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        title={title || undefined}
        style={baseStyle}
      >
        <option value="">— Select —</option>
        {col.edit_mask!.values!.map((opt, i) => (
          <option key={`${opt.data}-${i}`} value={opt.data}>{opt.display}</option>
        ))}
      </select>
    )
  }
  // Radio + checkbox masks are clumsy in a 120-px cell — fall back to a
  // dropdown of the same data/display options. The full form (popup edit
  // in list+form template) still gives users the original control type.
  if (hasRadioValues(col) || hasCheckboxValues(col)) {
    return (
      <select
        value={ddlbSelectValue(value, col.edit_mask!.values!)}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        title={title || undefined}
        style={baseStyle}
      >
        <option value="">— Select —</option>
        {col.edit_mask!.values!.map((opt, i) => (
          <option key={`${opt.data}-${i}`} value={opt.data}>{opt.display}</option>
        ))}
      </select>
    )
  }
  // bool_check edit-mask: render the same single binary checkbox the
  // form templates use, with the column's checked/unchecked data values
  // (slot 0 / slot 1). Must come before the plain `type === 'checkbox'`
  // branch so a bool_check column with col_type=BOOLEAN still maps the
  // value through edit_mask.values instead of writing raw booleans.
  if (hasBoolCheck(col)) {
    const { checked: checkedData, unchecked: uncheckedData } = boolCheckValues(col)
    const isOn = isBoolCheckOn(value, checkedData)
    return (
      <input
        type="checkbox"
        checked={isOn}
        onChange={e => onChange(e.target.checked ? checkedData : uncheckedData)}
        disabled={disabled}
        title={title || undefined}
        style={{ width: 18, height: 18, margin: '4px 0' }}
      />
    )
  }
  if (type === 'checkbox') {
    return (
      <input
        type="checkbox"
        checked={value === true || value === 'true' || value === 1}
        onChange={e => onChange(e.target.checked)}
        disabled={disabled}
        title={title || undefined}
        style={{ width: 18, height: 18, margin: '4px 0' }}
      />
    )
  }
  if (showLookup) {
    return (
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        {type === 'number' ? (
          // A number is typed as text (no spinner, the wheel never nudges it).
          <NumericInput
            value={value}
            placeholder={placeholder || 'Type or 🔍'}
            onChange={v => onChange(v)}
            disabled={disabled}
            title={title || (error || undefined)}
            style={{ ...baseStyle, flex: 1 }}
          />
        ) : (
          <input
            type={type}
            value={formatForInput(value, type)}
            placeholder={placeholder || 'Type or 🔍'}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            title={title || (error || undefined)}
            style={{ ...baseStyle, flex: 1 }}
          />
        )}
        <button
          type="button"
          onClick={() => { if (!disabled) setLookupOpen(true) }}
          disabled={disabled}
          title={`Open lookup: ${col.lookup}`}
          style={{
            padding: '2px 6px',
            border: '1px solid var(--color-border)',
            borderRadius: 3,
            background: 'var(--color-bg)',
            color: 'var(--color-text)',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        ><Search size={12} /></button>
        <LookupModal
          open={lookupOpen}
          project={project}
          objName={objName}
          lookup={col.lookup}
          fieldLabel={listHeadingOf(col)}
          title={col.lookup_title}
          addObject={col.lookup_add_object}
          dataCol={col.lookup_sql_data_col}
          onClose={() => setLookupOpen(false)}
          onSelect={(row) => { onChange(row.data); setLookupOpen(false) }}
          filter={col.lookup_filter}
          filterRules={col.lookup_filter_rules}
          lookupSql={col.lookup_sql}
          field={col.name}
          inheritedField={col.lookup_inherited_from?.field_name}
          formValues={rowValues}
          headerValues={headerValues}
        />
      </div>
    )
  }
  // Presentation widgets in a grid cell: URL field with open affordance, and
  // a compact image/document uploader. Textarea falls through to a text cell.
  if (hasUrlWidget(col)) {
    return (
      <UrlField
        value={value == null ? '' : String(value)}
        onChange={v => onChange(v)}
        disabled={disabled}
        style={baseStyle}
      />
    )
  }
  if (hasFileUpload(col)) {
    return (
      <FileUploadField
        kind={hasImageUpload(col) ? 'image' : 'document'}
        value={value == null ? '' : String(value)}
        onChange={v => onChange(v)}
        uploadFn={uploadDocument}
        accept={fileUploadAccept(col)}
        disabled={disabled}
        docType={columnDocType(col) || undefined}
      />
    )
  }
  // Dates are typed, not segmented — see `widgets/DateField`. A grid cell is
  // where this bit most, because a cell is left by tabbing on to the next one
  // and a segmented box that had not been completed committed nothing.
  if (type === 'date' || type === 'datetime-local') {
    return (
      <DateField
        type={type}
        value={value}
        onChange={v => onChange(v)}
        zoned={isZonedColumn(col)}
        disabled={disabled}
        title={title || (error || undefined)}
        style={baseStyle}
        compact
      />
    )
  }
  if (type === 'number') {
    // Typed as text with a decimal keypad: no spinner, and the mouse wheel
    // scrolls the grid instead of changing the value.
    return (
      <NumericInput
        value={value}
        placeholder={placeholder}
        onChange={v => onChange(v)}
        disabled={disabled}
        title={title || (error || undefined)}
        style={{ ...baseStyle, textAlign: 'right' }}
      />
    )
  }
  return (
    <input
      type={type}
      value={formatForInput(value, type)}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      title={title || (error || undefined)}
      style={baseStyle}
    />
  )
}

// Coerce a pasted / filled text value into the shape this column's editor
// would produce on change — so an Excel paste lands the same value a manual
// edit would. Mirrors InlineCellEditor's onChange coercion.
export function coerceCellText(col: ColumnDef, text: string): unknown {
  if (hasBoolCheck(col)) {
    const { checked, unchecked } = boolCheckValues(col)
    return isTruthyText(text, checked) ? checked : unchecked
  }
  if (inputType(col) === 'checkbox') return isTruthyText(text)
  if (inputType(col) === 'number') return text === '' ? '' : Number(text)
  // A pasted date lands the same canonical value the editor commits, so a
  // spreadsheet's `15/09/2026` is not stored as prose.
  if (inputType(col) === 'date' || inputType(col) === 'datetime-local') {
    const p = parseTypedDate(text, inputType(col))
    if (p === null) return text
    return p && isZonedColumn(col) ? withLocalZone(p) : p
  }
  // Dropdown / radio / checkbox-mask cells store the option's `data` string;
  // text / date / url etc. store the string as-is.
  return text
}

export function isTruthyText(text: string, checkedData?: unknown): boolean {
  const t = text.trim().toLowerCase()
  if (checkedData != null && t === String(checkedData).toLowerCase()) return true
  return ['true', '1', 'yes', 'y', 'on', 'x', 'checked'].includes(t)
}

// Compact cell-sized variant of FormRenderer.fieldStyle. The input fills its
// Excel cell with no border of its own (the td gridline frames it) so it reads
// as editing directly in a spreadsheet cell, and it inherits the theme so the
// cell is white-on-dark-text in the light theme and light-on-dark in the dark
// theme. Disabled/retrieved cells get the theme's secondary shade (blends with
// the surrounding theme rather than a hard grey).
export function cellInputStyle(error: boolean, disabled: boolean): React.CSSProperties {
  return {
    width: '100%', boxSizing: 'border-box',
    padding: '4px 6px',
    borderRadius: 0,
    border: error ? '1.5px solid #ef4444' : '1px solid transparent',
    // backgroundColor, not the `background` shorthand: the shorthand would
    // wipe the theme's chevron image off a <select> cell (appTheme.ts).
    backgroundColor: disabled ? 'var(--color-surface-2, var(--color-bg-secondary))' : 'transparent',
    color: 'var(--color-text)',
    opacity: 1,
    WebkitTextFillColor: 'var(--color-text)',
    fontSize: '0.9125rem',
    outline: 'none',
    boxShadow: error ? '0 0 0 2px rgba(239,68,68,0.15)' : 'none',
  }
}

// Re-exported, not reimplemented: this module's consumers (ChildTabularGrid)
// import it from here, and the rule itself belongs in one place — the comment
// that used to sit here said "Mirror FormRenderer.formatForInput", which is a
// duplicate announcing itself.
export { formatForInput }

